import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useMediaQuery, Box, Typography, Card, CardContent, TextField, InputAdornment, Pagination } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ChimeSound from '../../utils/assets/sound/chime.wav';
// import { DataContext } from '../../App';
import { StatusComponent } from './StatusComponent'; // Ensure the correct path to StatusComponent
import { useKeycloak } from '@react-keycloak/web';
import ApiConfig from '../../utils/services/apiConfig';
import NotificationPopup from './Notification';
import './App.css';
import Config from '../../config';
import { formatLocalDateTime } from '../../utils/dateTime';

const socket = new WebSocket(Config.robot_alert_ws);

const columns = [
  { field: 'Id', headerName: 'ID', flex: 0.2, minWidth: 150 },
  { field: 'Reported_By', headerName: 'Robot ID', flex: 0.3, minWidth: 180 },
  { field: 'Subcategory', headerName: 'Category', flex: 0.3 },
  { field: 'Description', headerName: 'Description', flex: 0.5 },
  { field: 'Status', headerName: 'Status', flex: 0.25, renderCell: (params) => <StatusComponent value={params.value} /> },
  { field: 'Site', headerName: 'Site', flex: 0.2 },
  { field: 'Location', headerName: 'Location', flex: 0.2 },
  { field: 'UpdatedAt', headerName: 'Updated Time', flex: 0.5, renderCell: (params) => {
    return formatLocalDateTime(params.value);
  } },
  { field: 'Timestamp', headerName: 'Reported Time', flex: 0.5, renderCell: (params) => {
    return formatLocalDateTime(params.value);
  } },
];

export default function AlertTable (props){
  const [audio] = useState(new Audio(ChimeSound));
  const [newAlert, setNewAlert] = useState({})
  const [showNotification, setShowNotification] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Initialize state from URL params to avoid race conditions
  const initialPage = parseInt(searchParams.get('page') ?? '0', 10);
  const initialPageSize = parseInt(searchParams.get('pageSize') ?? '10', 10);
  const initialSearch = searchParams.get('search') ?? '';

  const [searchText, setSearchText] = useState(initialSearch);
  const isMobileOrTablet = useMediaQuery('(max-width: 960px)'); // Switch layout for mobile and tablet
  const [alertData, setAlertData] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: Number.isNaN(initialPage) ? 0 : initialPage,
    pageSize: Number.isNaN(initialPageSize) ? 10 : initialPageSize,
  });
  const [totalCount, setTotalCount] = useState(0);
  const { keycloak } = useKeycloak();
  const apiConfig = useMemo(() => new ApiConfig(keycloak).api(), [keycloak]);

  socket.onopen = () => {
    console.log('WebSocket connection established.');
  };
  socket.onclose = () => {
    console.log('Websocket connection closed.')
    console.log('Reconnecting in 3 seconds...')
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  }
  socket.onmessage = (event) => {
    const obj = JSON.parse(event.data);
    console.log('Received alert:', obj);
    setAlertData(prevData => {
      const newData = [obj, ...prevData];
      return newData.slice(0, paginationModel.pageSize); // Keep only the first 'pageSize' items
    });
    setNewAlert(obj)
    setShowNotification(true)
    // handleSoundAlert()
  };

  const buildAlertsQuery = () => {
    const params = new URLSearchParams();
    params.set('page', paginationModel.page);
    params.set('pageSize', paginationModel.pageSize);
    if (searchText) params.set('search', searchText);
    return `?${params.toString()}`;
  };

  const handleRowClick = (params) => {
    const query = buildAlertsQuery();
    navigate(`/alerts/${params.Id}`, { state: { from: 'alerts', alertsQuery: query, returnTo: `/alerts${query}` } });
  };

  const handleSearch = (event) => {
    setSearchText(event.target.value);
  };

  const getAlerts = useCallback(() => {
    Promise.all([
      apiConfig.fetchAlertsPage(paginationModel.page + 1, paginationModel.pageSize, searchText.toLowerCase()),
    ]).then(results => {
      // console.log(results[0])
      setAlertData(results[0].results.RobotResponse)
      setTotalCount(results[0].count);
    }).catch(error => {
      console.error(error);
    });
  }, [apiConfig, paginationModel.page, paginationModel.pageSize, searchText]);

  const handlePageChange = (newModel) => {
    console.log(newModel);
    setPaginationModel(newModel);
  };

  useEffect(() => {
    // Play a sound when alertdata changed from websocket or when alerts page is navigated to
    audio.play().catch(error => console.error("Playback error:", error));
  }, [newAlert, audio]);

  // keep URL in sync when paginationModel or searchText changes
  useEffect(() => {
    const q = (() => {
      const params = new URLSearchParams();
      params.set('page', paginationModel.page);
      params.set('pageSize', paginationModel.pageSize);
      if (searchText) params.set('search', searchText);
      return `?${params.toString()}`;
    })();
    // update the URL without adding a new history entry
    navigate(`/alerts${q}`, { replace: true });
    // fetch alerts for current state
    getAlerts();
  }, [paginationModel, searchText, navigate, getAlerts]);

  return (
    <div className="alert-table-container">
      <NotificationPopup show={showNotification} alert={newAlert} />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        sx={{
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          gap: {
            xs: 2,
            sm: 0,
          },
        }}
      >
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontSize: {
              xs: 22,
              sm: 30,
              md: 50,
            },
            textAlign: {
              xs: 'center',
              sm: 'left',
            },
          }}
        >
          Alerts
        </Typography>

        <TextField
          variant="outlined"
          placeholder="Search alerts"
          onChange={handleSearch}
          value={searchText}
          sx={{
            width: {
              xs: '100%',
              sm: '300px',
            },
            backgroundColor: 'background.paper',
            borderRadius: '5px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '5px',
              height: {
                xs: '40px',
                sm: '50px',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isMobileOrTablet ? (
        // Card layout for mobile and tablet
        <>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr', // One card per row for smaller screens
            sm: '1fr 1fr', // Two cards per row for slightly larger screens
          }}
          gap={2}
        >
          {alertData.map((row) => (
            <Card
              key={row.Id}
              sx={{
                borderRadius: '12px',
                boxShadow: 2,
                cursor: 'pointer',
                '&:hover': { boxShadow: 6, transform: 'scale(1.02)' },
                transition: 'all 0.2s ease-in-out',
              }}
              // onClick={() => handleCardClick(row.id)}
              onClick={()=>{
                const query = buildAlertsQuery();
                navigate(`/alerts/${row.Id}`, { state: { from: 'alerts', alertsQuery: query, returnTo: `/alerts${query}` } });
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight="bold">
                  Robot ID: {row.Reported_By}
                </Typography>
                <Typography variant="body1">
                  <strong>Category:</strong> {row.Subcategory}
                </Typography>
                <Typography variant="body1">
                  <strong>Description:</strong> {row.Description}
                </Typography>
                <Typography variant="body1">
                  <strong>Status:</strong>{' '}
                  <StatusComponent value={row.Status} />
                </Typography>
                <Typography variant="body1">
                  <strong>Site:</strong> {row.Site}
                </Typography>
                <Typography variant="body1">
                  <strong>Location:</strong> {row.Location}
                </Typography>
                <Typography variant="body1">
                  <strong>Updated Time:</strong> {formatLocalDateTime(row.UpdatedAt)}
                </Typography>
                <Typography variant="body1">
                  <strong>Reported Time:</strong> {formatLocalDateTime(row.Timestamp)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={Math.ceil(totalCount / paginationModel.pageSize) || 1}
            page={paginationModel.page + 1}
            onChange={(_, value) => setPaginationModel(prev => ({ ...prev, page: value - 1 }))}
            color="primary"
          />
        </Box>
        </>
      ) : (
        // Table layout for larger screens
        <div className="alert-data-grid">
          <DataGrid
            rows={alertData}
            columns={columns}
            getRowId={(row) => row.Id}
            // initialState={{
            //   pagination: { paginationModel: { page: 0, pageSize: 10 } },
            // }}
            rowCount={totalCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePageChange}
            pageSizeOptions={[5, 10, 25]}
            onRowClick={(params) => handleRowClick(params.row)}
            // getRowClassName={(params) =>
            //   params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
            // }
            sx={{
              backgroundColor: 'background.default',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          />
          {/* Desktop pagination control to mirror mobile Pagination */}
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.max(1, Math.ceil(totalCount / paginationModel.pageSize))}
              page={paginationModel.page + 1}
              onChange={(_, value) => setPaginationModel(prev => ({ ...prev, page: value - 1 }))}
              color="primary"
            />
          </Box>
        </div>
      )}
    </div>
  );
};
